<template>
  <b-card title="Ajouter un ramasseur">
    <b-overlay
      :show="isLoading"
      rounded="lg"
      spinner-variant="primary"
    >
      <validation-observer
        ref="formValidator"
        #default="{ handleSubmit }"
      >
        <b-form @submit.prevent="handleSubmit(submitForm)">
          <b-card>
            <h5 class="mb-3">
              <feather-icon
                icon="UserIcon"
                class="mr-1"
              />
              Informations personnelles
            </h5>

            <b-row>
              <!-- Username -->
              <b-col md="6">
                <b-form-group label-class="form-label-premium">
                  <template #label>
                    Nom du ramasseur <span class="text-danger">*</span>
                  </template>
                  <validation-provider
                    #default="{ errors, valid }"
                    name="Nom"
                    rules="required"
                    mode="eager"
                  >
                    <div class="position-relative">
                      <b-form-input
                        v-model="formData.username"
                        :state="errors.length > 0 ? false : (valid && formData.username ? true : null)"
                        placeholder="Entrez le nom complet"
                        class="premium-input"
                      />
                      <feather-icon
                        v-if="valid && formData.username"
                        icon="CheckCircleIcon"
                        size="18"
                        class="input-success-icon"
                      />
                    </div>
                    <small
                      v-if="errors[0]"
                      class="text-danger validation-message-animate"
                    >{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <!-- Email -->
              <b-col md="6">
                <b-form-group label-class="form-label-premium">
                  <template #label>
                    Email <span class="text-danger">*</span>
                  </template>
                  <validation-provider
                    #default="{ errors, valid }"
                    name="Email"
                    rules="required|email|asyncEmail"
                    mode="eager"
                  >
                    <b-input-group class="input-group-merge">
                      <b-form-input
                        v-model="formData.email"
                        type="email"
                        :state="errors.length > 0 ? false : (valid && formData.email ? true : null)"
                        placeholder="Entrez l'email"
                        class="premium-input"
                      />
                      <b-input-group-append is-text>
                        <feather-icon
                          v-b-tooltip.hover="emailGenerationTooltip"
                          icon="RefreshCwIcon"
                          :class="formData.username && formData.username.trim().length > 0 ? 'cursor-pointer text-primary' : 'text-muted cursor-not-allowed'"
                          @click="formData.username && formData.username.trim().length > 0 ? generateCompanyEmail() : null"
                        />
                      </b-input-group-append>
                    </b-input-group>
                    <small
                      v-if="errors[0]"
                      class="text-danger validation-message-animate"
                    >{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <!-- Phone -->
              <b-col md="6">
                <b-form-group label-class="form-label-premium">
                  <template #label>
                    Téléphone <span class="text-danger">*</span>
                  </template>
                  <validation-provider
                    #default="{ errors, valid }"
                    name="Téléphone"
                    rules="required|tnPhone|asyncPhone"
                    mode="eager"
                  >
                    <div class="position-relative">
                      <b-form-input
                        id="phone"
                        v-model="formData.phone"
                        :formatter="formatPhone"
                        :state="errors.length > 0 ? false : (valid && formData.phone ? true : null)"
                        placeholder="Entrez le numéro de téléphone"
                        maxlength="8"
                        class="premium-input"
                        @keypress="allowOnlyNumbers"
                      />
                      <feather-icon
                        v-if="valid && formData.phone"
                        icon="CheckCircleIcon"
                        size="18"
                        class="input-success-icon"
                      />
                    </div>
                    <small
                      v-if="errors[0]"
                      class="text-danger validation-message-animate"
                    >{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <!-- Phone 2 -->
              <b-col md="6">
                <b-form-group
                  label-class="form-label-premium"
                  label="Téléphone secondaire (optionnel)"
                >
                  <b-form-input
                    id="phone2"
                    v-model="formData.phone2"
                    class="premium-input"
                    :formatter="formatPhone"
                    placeholder="Entrez le numéro de téléphone secondaire"
                    maxlength="8"
                    @keypress="allowOnlyNumbers"
                  />
                </b-form-group>
              </b-col>

              <!-- Address -->
              <b-col md="6">
                <b-form-group
                  label-class="form-label-premium"
                  label="Adresse"
                >
                  <b-form-textarea
                    v-model="formData.address"
                    placeholder="Entrez l'adresse"
                    rows="3"
                  />
                </b-form-group>
              </b-col>

              <!-- Car -->
              <b-col md="6">
                <b-form-group
                  label-class="form-label-premium"
                  label="Véhicule"
                >
                  <v-select
                    v-model="formData.car"
                    class="premium-input"
                    :options="carOptions"
                    label="displayName"
                    :reduce="car => car.id"
                    placeholder="Sélectionnez un véhicule (optionnel)"
                    clearable
                  />
                </b-form-group>
              </b-col>

              <!-- Repository -->
              <b-col md="6">
                <b-form-group
                  label-class="form-label-premium"
                  label="Dépôt"
                >
                  <v-select
                    v-model="formData.repository"
                    class="premium-input"
                    :options="repositories"
                    label="name"
                    :reduce="repo => repo.id"
                    placeholder="Sélectionnez un dépôt (optionnel)"
                    clearable
                  />
                </b-form-group>
              </b-col>
            </b-row>

            <h5 class="mt-3 mb-3">
              <feather-icon
                icon="LockIcon"
                class="mr-1"
              />
              Informations d'accès
            </h5>

            <b-row>
              <!-- Password -->
              <b-col md="6">
                <b-form-group label-class="form-label-premium">
                  <template #label>
                    Mot de passe <span class="text-danger">*</span>
                  </template>
                  <validation-provider
                    #default="{ errors, valid }"
                    name="Mot de passe"
                    rules="required|min:8"
                    vid="password"
                    mode="eager"
                  >
                    <div class="position-relative">
                      <b-input-group class="input-group-merge">
                        <b-form-input
                          v-model="formData.password"
                          :type="showPassword ? 'text' : 'password'"
                          :state="errors.length > 0 ? false : (valid && formData.password ? true : null)"
                          placeholder="Entrez le mot de passe"
                          class="premium-input"
                        />
                        <b-input-group-append is-text>
                          <feather-icon
                            :icon="showPassword ? 'EyeOffIcon' : 'EyeIcon'"
                            class="cursor-pointer"
                            @click="showPassword = !showPassword"
                          />
                        </b-input-group-append>
                      </b-input-group>
                      <feather-icon
                        v-if="valid && formData.password"
                        icon="CheckCircleIcon"
                        size="18"
                        class="input-success-icon password-icon"
                        style="right: 3.5rem;"
                      />
                    </div>
                    <small
                      v-if="errors[0]"
                      class="text-danger validation-message-animate"
                    >{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <!-- Confirm Password -->
              <b-col md="6">
                <b-form-group label-class="form-label-premium">
                  <template #label>
                    Confirmer le mot de passe <span class="text-danger">*</span>
                  </template>
                  <validation-provider
                    #default="{ errors, valid }"
                    name="Confirmation du mot de passe"
                    rules="required|confirmed:password"
                    mode="eager"
                  >
                    <div class="position-relative">
                      <b-input-group class="input-group-merge">
                        <b-form-input
                          v-model="formData.confirm_password"
                          :type="showConfirmPassword ? 'text' : 'password'"
                          :state="errors.length > 0 ? false : (valid && formData.confirm_password ? true : null)"
                          placeholder="Confirmez le mot de passe"
                          class="premium-input"
                        />
                        <b-input-group-append is-text>
                          <feather-icon
                            :icon="showConfirmPassword ? 'EyeOffIcon' : 'EyeIcon'"
                            class="cursor-pointer"
                            @click="showConfirmPassword = !showConfirmPassword"
                          />
                        </b-input-group-append>
                      </b-input-group>
                      <feather-icon
                        v-if="valid && formData.confirm_password"
                        icon="CheckCircleIcon"
                        size="18"
                        class="input-success-icon password-icon"
                        style="right: 3.5rem;"
                      />
                    </div>
                    <small
                      v-if="errors[0]"
                      class="text-danger validation-message-animate"
                    >{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
            </b-row>

            <!-- Generate Password Button -->
            <div class="text-center mb-3 mt-2">
              <PasswordGenerator
                :form-data="formData"
                user-type="pickupeur"
                @generated="onPasswordGenerated"
              />
            </div>

            <div class="d-flex justify-content-between mt-3">
              <b-button
                variant="outline-secondary"
                :to="{ name: 'pickupeur-list' }"
              >
                <feather-icon
                  icon="XIcon"
                  class="mr-1"
                />
                Annuler
              </b-button>

              <span
                id="pickupeur-create-save-btn-wrapper"
                class="d-inline-block"
              >
                <b-button
                  variant="primary"
                  type="submit"
                  :disabled="!isFormValid || isSubmitting"
                  :class="{ 'not-allowed': !isFormValid || isSubmitting }"
                >
                  <b-spinner
                    v-if="isSubmitting"
                    small
                    class="mr-1"
                  />
                  <feather-icon
                    v-else
                    icon="SaveIcon"
                    class="mr-1"
                  />
                  Enregistrer
                </b-button>
              </span>
              <b-tooltip
                v-if="!isFormValid || isSubmitting"
                target="pickupeur-create-save-btn-wrapper"
                triggers="hover"
                placement="top"
              >
                {{ tooltipMessage }}
              </b-tooltip>
            </div>
          </b-card>
        </b-form>
      </validation-observer>
    </b-overlay>
  </b-card>
</template>

<script>
import { extend, ValidationProvider, ValidationObserver } from 'vee-validate'
import {
  required, email, numeric, length, min, confirmed,
} from 'vee-validate/dist/rules'
import {
  BCard,
  BForm,
  BFormGroup,
  BFormInput,
  BRow,
  BCol,
  BButton,
  BInputGroup,
  BInputGroupAppend,
  BSpinner,
  BOverlay,
  BFormTextarea,
  BTooltip,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import PasswordGenerator from '@core/components/password/PasswordGenerator.vue'
import axios from 'axios'

extend('required', required)
extend('email', email)
extend('numeric', numeric)
extend('length', length)
extend('min', min)
extend('confirmed', confirmed)

export default {
  components: {
    BCard,
    ValidationProvider,
    ValidationObserver,
    PasswordGenerator,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BRow,
    BCol,
    BButton,
    BInputGroup,
    BInputGroupAppend,
    BSpinner,
    BOverlay,
    BTooltip,
    vSelect,
  },

  data() {
    return {
      isLoading: false,
      isSubmitting: false,
      cars: [],
      repositories: [],
      showPassword: false,
      showConfirmPassword: false,
      formData: {
        username: '',
        email: '',
        phone: '',
        phone2: '',
        address: '',
        car: null,
        repository: null,
        password: '',
        confirm_password: '',
      },
    }
  },

  computed: {
    companyDomain() {
      return process.env.VUE_APP_COMPANY_EMAIL_DOMAIN || null
    },

    emailGenerationTooltip() {
      if (!this.companyDomain) {
        return 'Erreur: Variable d\'environnement VUE_APP_COMPANY_EMAIL_DOMAIN non définie'
      }
      if (this.formData.username && this.formData.username.trim().length > 0) {
        return `Générer un email par défaut à partir du nom (@${this.companyDomain}). Modifiable si nécessaire.`
      }
      return 'Veuillez d\'abord saisir le nom du ramasseur'
    },

    carOptions() {
      if (!Array.isArray(this.cars)) return []
      return this.cars.map(car => ({
        ...car,
        displayName: `${car.brand || 'N/A'} - ${car.serial_number || 'N/A'} (${car.color || 'N/A'})`,
      }))
    },

    isFormValid() {
      return (
        this.formData.username !== ''
        && this.formData.email !== ''
        && this.formData.phone !== ''
        && this.formData.password !== ''
        && this.formData.password.length >= 8
        && this.formData.confirm_password !== ''
        && this.formData.password === this.formData.confirm_password
      )
    },

    tooltipMessage() {
      if (this.isSubmitting) return 'Enregistrement en cours...'

      const missing = []
      if (!this.formData.username || !this.formData.username.trim()) missing.push('Nom')
      if (!this.formData.email || !this.formData.email.trim()) missing.push('Email')
      if (!this.formData.phone || !this.formData.phone.trim()) missing.push('Téléphone')
      if (!this.formData.password || this.formData.password.length < 8) missing.push('Mot de passe')
      if (!this.formData.confirm_password || this.formData.password !== this.formData.confirm_password) missing.push('Confirmation mot de passe')

      if (missing.length === 0) return ''
      if (missing.length === 1) {
        return `Veuillez renseigner le champ : ${missing[0]}.`
      }
      if (missing.length <= 3) {
        return `Veuillez remplir les champs obligatoires : ${missing.join(', ')}.`
      }
      return `Veuillez remplir les champs obligatoires manquants (${missing.length} champs).`
    },
  },

  async created() {
    this.isLoading = true
    try {
      await Promise.all([this.fetchCars(), this.fetchRepositories()])
    } finally {
      this.isLoading = false
    }
  },

  methods: {
    allowOnlyNumbers(event) {
      const { charCode, target } = event
      if (charCode < 48 || charCode > 57) {
        event.preventDefault()
      }
      if (target && target.id === 'phone' && (this.formData?.phone || this.phone)?.length >= 8) {
        event.preventDefault()
      }
      if (target && target.id === 'phone2' && (this.formData?.phone2 || this.phone2)?.length >= 8) {
        event.preventDefault()
      }
    },
    formatPhone(value) {
      return value.replace(/\D/g, '').substring(0, 8)
    },

    async fetchRepositories() {
      try {
        const response = await axios.get('/api/repositories/')
        if (Array.isArray(response.data)) {
          this.repositories = response.data
        } else if (response.data && Array.isArray(response.data.results)) {
          this.repositories = response.data.results
        }
      } catch (error) {
        this.repositories = []
        // eslint-disable-next-line no-console
        console.error('Error fetching repositories:', error)
      }
    },

    async fetchCars() {
      try {
        const response = await axios.get('/api/cars/')
        let cars = []
        if (Array.isArray(response.data)) {
          cars = response.data
        } else if (response.data && Array.isArray(response.data.results)) {
          cars = response.data.results
        }
        this.cars = cars
      } catch (error) {
        this.cars = []
        this.showToast('danger', 'Erreur lors du chargement des véhicules')
      }
    },

    async submitForm() {
      this.isSubmitting = true

      try {
        const payload = {
          username: this.formData.username,
          email: this.formData.email,
          phone: this.formData.phone,
          phone2: this.formData.phone2,
          address: this.formData.address,
          car: this.formData.car,
          repository: this.formData.repository,
          password: this.formData.password,
        }

        await axios.post('/api/auth/register/pickupeur/', payload)

        this.showToast('success', 'Ramasseur ajouté avec succès')
        this.$router.push({ name: 'pickupeur-list' })
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error creating pickupeur:', error)

        let errorMessage = 'Erreur lors de la création du ramasseur'

        if (error.response && error.response.data) {
          const errorData = error.response.data

          if (typeof errorData === 'object' && errorData !== null) {
            const messages = []
            // Handle both { field: [error] } and { errors: { field: [error] } }
            const fieldsToProcess = (errorData.errors && typeof errorData.errors === 'object')
              ? errorData.errors
              : errorData

            Object.keys(fieldsToProcess).forEach(field => {
              const fieldError = fieldsToProcess[field]
              const rawError = Array.isArray(fieldError) ? fieldError[0] : fieldError
              if (typeof rawError === 'string') {
                if (rawError.includes('existe') || rawError.includes('already exists')) {
                  if (field === 'phone') messages.push('Ce numéro de téléphone est déjà utilisé.')
                  else if (field === 'email') messages.push('Cette adresse email est déjà utilisée.')
                  else if (field === 'username') messages.push('Ce nom est déjà utilisé.')
                  else messages.push(`Le champ ${field} est déjà utilisé.`)
                } else {
                  messages.push(rawError)
                }
              }
            })

            if (messages.length > 0) {
              errorMessage = messages.join(' ')
            } else if (errorData.message) {
              errorMessage = errorData.message
            } else if (errorData.detail) {
              errorMessage = errorData.detail
            }
          } else if (typeof errorData === 'string') {
            errorMessage = errorData
          }
        }

        this.showToast('danger', errorMessage)
      } finally {
        this.isSubmitting = false
      }
    },

    showToast(variant, text) {
      let title
      let icon

      if (variant === 'success') {
        title = 'Succès'
        icon = 'CheckCircleIcon'
      } else if (variant === 'warning') {
        title = 'Attention'
        icon = 'AlertTriangleIcon'
      } else {
        title = 'Erreur'
        icon = 'XCircleIcon'
      }

      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          icon,
          text,
          variant,
        },
      })
    },

    stripAccents(value) {
      if (!value) return ''
      return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    },

    generateCompanyEmail() {
      // Get username to generate email (button is disabled if empty, so no need to check)
      const { username } = this.formData

      // Clean the username - remove spaces, accents, special chars
      const cleanUsername = this.stripAccents(username.trim())
        .toLowerCase()
        .replace(/\s+/g, '.') // Replace spaces with dots
        .replace(/[^a-z0-9.]/g, '') // Remove non-alphanumeric except dots

      // Get company domain from env variable, store, or default
      const domain = this.companyDomain

      // Generate email
      const generatedEmail = `${cleanUsername}@${domain}`
      this.formData.email = generatedEmail

      this.showToast('success', `Email généré: ${generatedEmail}`)
    },

    onPasswordGenerated(generatedPassword) {
      this.formData.password = generatedPassword
      this.formData.confirm_password = generatedPassword
      this.showPassword = true
      this.showConfirmPassword = true
      this.$forceUpdate()
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

.cursor-pointer {
  cursor: pointer;
}

/* Premium Input Styling with Real-time Validation */
.premium-input {
  transition: all 0.3s ease;
  padding: 0.75rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;

  &:focus {
    border-color: #9b1c22;
    box-shadow: 0 0 0 3px rgba(13, 67, 153, 0.15);
  }

  &.is-valid {
    border-color: #28c76f !important;
    background-color: #f0fdf4;
    padding-right: 3rem;

    &:focus {
      border-color: #28c76f !important;
      box-shadow: 0 0 0 3px rgba(40, 199, 111, 0.15);
    }
  }

  &.is-invalid {
    border-color: #ea5455 !important;
    background-color: #fef2f2;

    &:focus {
      border-color: #ea5455 !important;
      box-shadow: 0 0 0 3px rgba(234, 84, 85, 0.15);
    }
  }
}

.input-success-icon {
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #28c76f;
  pointer-events: none;
  z-index: 5;
  animation: checkFadeIn 0.3s ease;

  &.textarea-icon {
    top: 1rem;
    transform: none;
  }

  &.select-icon {
    right: 3rem;
  }

  &.password-icon {
    right: 3.5rem;
  }
}

@keyframes checkFadeIn {
  from {
    opacity: 0;
    transform: translateY(-50%) scale(0.5);
  }
  to {
    opacity: 1;
    transform: translateY(-50%) scale(1);
  }
}

.validation-message-animate {
  display: inline-flex;
  align-items: center;
  animation: fadeInDown 0.3s ease;
  margin-top: 0.25rem;
}

@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* V-Select Real-time Validation */
.premium-select {
  &.is-valid .vs__dropdown-toggle {
    border: 2px solid #28c76f !important;
    background-color: #f0fdf4;
  }

  &.is-invalid .vs__dropdown-toggle {
    border: 2px solid #ea5455 !important;
    background-color: #fef2f2;
  }
}

/* Submit Button Cursor Feedback */
.btn.not-allowed,
.btn:disabled {
  cursor: not-allowed !important;
  opacity: 0.6 !important;
  pointer-events: auto !important;
}

/* Generate Password Button Styling */
.generate-password-btn-centered {
  padding: 0.75rem 2rem;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.9375rem;
  border: 2px solid #9b1c22;
  color: #9b1c22;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(13, 67, 153, 0.1);

  &:hover {
    background: linear-gradient(135deg, #9b1c22 0%, #0a3370 100%);
    color: #ffffff;
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(13, 67, 153, 0.3);
    border-color: #9b1c22;
  }

  &:active {
    transform: translateY(0);
  }
}
</style>
